import type { UiAction } from '@massilia/queries'

import { useStreamChatMutation } from '@massilia/queries'
import { cn } from '@massilia/ui'
import { useNavigate } from '@tanstack/react-router'
import { ExternalLink, MessageCircle, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { useChatWidgetStore } from '@/stores/chat-widget.store'

interface ChatWidgetProps {
  className?: string
}

const QUICK_MESSAGES = [
  {
    emoji: '✈️',
    isGold: true,
    text: "Trouver le billet d'avion le moins cher directement avec l'aide de l'IA",
  },
  {
    emoji: '🗺️',
    isGold: false,
    text: 'Comment organiser mon voyage avec Massilia Voyages ?',
  },
  {
    emoji: '🌍',
    isGold: false,
    text: 'Guide pour une destination particulière',
  },
  {
    emoji: '💬',
    isGold: false,
    text: 'Un problème particulier ? Posez la question directement',
  },
] as const

interface UiActionRendererProps {
  action: UiAction
  isSending: boolean
  onButtonClick: (value: string) => void
  onOpenFlightResults: (query: string) => void
}

// ---------------------------------------------------------------------------
// UiAction renderer
// ---------------------------------------------------------------------------

export function ChatWidget({ className }: ChatWidgetProps) {
  const streamChatMutation = useStreamChatMutation()
  const navigate = useNavigate()
  const chatMode = useChatWidgetStore((state) => state.chatMode)
  const error = useChatWidgetStore((state) => state.error)
  const input = useChatWidgetStore((state) => state.input)
  const isOpen = useChatWidgetStore((state) => state.isOpen)
  const isWaitingForFirstChunk = useChatWidgetStore((state) => state.isWaitingForFirstChunk)
  const isSending = useChatWidgetStore((state) => state.isSending)
  const messages = useChatWidgetStore((state) => state.messages)
  const sendMessage = useChatWidgetStore((state) => state.sendMessage)
  const canOpenFlightResults = useChatWidgetStore((state) => state.canOpenFlightResults)
  const handleUiButtonClick = useChatWidgetStore((state) => state.handleUiButtonClick)
  const setInput = useChatWidgetStore((state) => state.setInput)
  const resetConversation = useChatWidgetStore((state) => state.resetConversation)
  const setShowQuickActions = useChatWidgetStore((state) => state.setShowQuickActions)
  const showQuickActions = useChatWidgetStore((state) => state.showQuickActions)
  const toggleOpen = useChatWidgetStore((state) => state.toggleOpen)
  const waitingStageLabel = useChatWidgetStore((state) => state.waitingStageLabel)

  // Used to autoscroll when new messages are appended.
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const prevIsOpenRef = useRef(isOpen)
  const quickActionsRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!messagesEndRef.current) {
      return
    }

    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    requestAnimationFrame(() => {
      if (showQuickActions && !isSending) {
        quickActionsRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
        return
      }

      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [isOpen, isSending, showQuickActions])

  useEffect(() => {
    const didReopen = isOpen && !prevIsOpenRef.current
    if (didReopen && !isSending) {
      setShowQuickActions(true)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, isSending, setShowQuickActions])

  const SCROLL_THRESHOLD_PX = 100
  const isNearBottom = () => {
    const el = scrollContainerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX
  }

  const onAssistantChunkRendered = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    })
  }

  const sendCurrentInput = async () => {
    await sendMessage({
      isNearBottom,
      onAssistantChunkRendered,
      streamAssistantReply: streamChatMutation.mutateAsync,
    })
  }

  const handleQuickMessage = async (text: string) => {
    if (isSending) {
      return
    }

    setShowQuickActions(false)
    setInput(text)
    await sendCurrentInput()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (input.trim()) {
      setShowQuickActions(false)
    }
    await sendCurrentInput()
  }

  const handleUiButtonValue = async (value: string) => {
    if (isSending) return
    setShowQuickActions(false)
    await handleUiButtonClick({
      isNearBottom,
      onAssistantChunkRendered,
      streamAssistantReply: streamChatMutation.mutateAsync,
      value,
    })
  }

  const shouldShowQuickActions = showQuickActions && !isSending

  const loadingLabel = waitingStageLabel ??
    (chatMode === 'flight_request'
      ? 'Je vérifie que la requête de vol est complète...'
      : 'Je regarde sur les pages du site')

  return (
    <>
      {isOpen ? (
        <div
          className={cn(
            'fixed right-4 bottom-24 z-50 flex h-[78vh] max-h-[860px] w-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl',
            'backdrop-blur-sm',
            className
          )}
        >
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1D95AD] text-white">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-stone-900">Assistant Massilia</span>
                <span className="text-xs text-stone-500">
                  {chatMode === 'flight_request' ? '✈️ Recherche de vol' : 'Posez-nous vos questions'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="rounded-full border border-[#1D95AD]/30 bg-white px-2.5 py-1 text-[10px] font-medium text-[#1D95AD] transition hover:bg-[#1D95AD]/10"
                disabled={isSending}
                onClick={resetConversation}
                type="button"
              >
                Réinitialiser discussion
              </button>
              <button
                aria-label="Fermer le chat"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-700"
                onClick={toggleOpen}
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-1 space-y-2 overflow-y-auto bg-white px-3 py-2 text-[13px]"
          >
            {messages.length > 0
              ? messages.map((message, index) => (
                  <div key={`${message.role}-${index}-${message.content.slice(0, 16)}`}>
                    <div className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                      <div
                        className={cn(
                          'max-w-[80%] rounded-2xl px-3 py-2 text-[13px] shadow-sm',
                          message.role === 'user'
                            ? 'rounded-br-sm bg-[#1D95AD] text-white'
                            : 'rounded-bl-sm bg-gray-100 text-stone-800'
                        )}
                      >
                        {message.role === 'assistant' &&
                        message.content === '' &&
                        isWaitingForFirstChunk ? (
                          <div className="flex items-center gap-2 text-xs text-stone-600">
                            <span
                              aria-hidden="true"
                              className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-stone-300 border-t-[#1D95AD]"
                            />
                            <span>{loadingLabel}</span>
                          </div>
                        ) : message.role === 'assistant' ? (
                          <div className="space-y-2 break-words">
                            <ChatMarkdown content={message.content} />
                          </div>
                        ) : (
                          <p className="break-words whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                    </div>

                    {/* Render uiActions below the assistant bubble */}
                    {message.role === 'assistant' &&
                      message.uiActions &&
                      message.uiActions.length > 0 &&
                      index === messages.length - 1 && (
                        <div className="mt-2 flex flex-col gap-2 pl-1">
                          {message.uiActions.map((action, actionIndex) => (
                            <UiActionRenderer
                              key={actionIndex}
                              action={action}
                              isSending={isSending}
                              onButtonClick={handleUiButtonValue}
                              onOpenFlightResults={(query) => {
                                if (!canOpenFlightResults(query)) return
                                void navigate({
                                  search: { q: query },
                                  to: '/flightsmanyquery/results',
                                })
                              }}
                            />
                          ))}
                        </div>
                      )}
                  </div>
                ))
              : null}

            {shouldShowQuickActions ? (
              <div
                ref={quickActionsRef}
                className={cn('flex flex-col gap-3 px-1', messages.length > 0 ? 'mt-6' : 'mt-2')}
              >
                {messages.length > 0 ? (
                  <div className="mt-2 mb-1 flex items-center gap-2">
                    <div className="h-px flex-1 bg-[#1D95AD]/40" />
                    <span className="text-[10px] font-semibold tracking-wide text-[#1D95AD] uppercase">
                      Switch
                    </span>
                    <div className="h-px flex-1 bg-[#1D95AD]/40" />
                  </div>
                ) : null}
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs leading-relaxed text-stone-700">
                  <p className="mb-1 text-sm font-semibold text-stone-900">
                    Bonjour et bienvenue chez Massilia !
                  </p>
                  <p>
                    Je suis ici pour vous aider à construire le voyage de vos rêves. Je peux vous
                    guider vers nos circuits, séjours ou croisières, répondre à vos questions, ou
                    même vous orienter vers l&apos;un de nos spécialistes.
                  </p>
                  <p className="mt-2 font-medium text-stone-800">
                    Que cherchez-vous aujourd&apos;hui ?
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_MESSAGES.map(({ emoji, isGold, text }) => (
                    <button
                      key={text}
                      className={cn(
                        'inline-flex min-h-11 items-start gap-1.5 rounded-2xl border px-2.5 py-1.5 text-left text-[11px] font-medium shadow-sm transition',
                        'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
                        isGold
                          ? 'col-span-2 min-h-12 border-amber-300 bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-400 text-amber-900 ring-1 ring-amber-300/70 hover:brightness-105 focus-visible:ring-amber-500'
                          : 'border-[#1D95AD]/30 bg-white text-[#1D95AD] hover:bg-[#1D95AD]/10 focus-visible:ring-[#1D95AD]'
                      )}
                      disabled={isSending}
                      onClick={() => void handleQuickMessage(text)}
                      type="button"
                    >
                      <span aria-hidden="true" className="shrink-0 text-sm leading-5">
                        {emoji}
                      </span>
                      <span className="leading-5">{text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {error ? (
              <p className="mt-2 text-xs text-red-500">
                {error}
                {error && !error.endsWith('.') ? '.' : null} Veuillez réessayer.
              </p>
            ) : null}
            <div ref={messagesEndRef} />
          </div>

          <form
            className="border-t border-gray-200 bg-gray-50 px-3 py-2"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <div className="flex items-end gap-2">
              <textarea
                aria-label="Votre message"
                className="max-h-24 min-h-[2.25rem] flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13px] text-stone-900 shadow-sm ring-0 outline-none placeholder:text-stone-400 focus:border-[#1D95AD] focus:ring-2 focus:ring-[#1D95AD]/20"
                disabled={isSending}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void handleSubmit(event as unknown as React.FormEvent)
                  }
                }}
                placeholder={
                  chatMode === 'flight_request'
                    ? 'Décrivez votre vol (destinations, dates, passagers)...'
                    : 'Écrivez votre message...'
                }
                rows={1}
                value={input}
              />
              <button
                aria-label="Envoyer le message"
                className={cn(
                  'inline-flex items-center justify-center rounded-lg bg-[#1D95AD] px-3 py-1.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-[#1a8599]',
                  isSending || !input.trim()
                    ? 'cursor-not-allowed opacity-60 hover:bg-[#1D95AD]'
                    : ''
                )}
                disabled={isSending || !input.trim()}
                type="submit"
              >
                {isSending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <button
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Fermer le widget de chat' : 'Ouvrir le widget de chat'}
        className={cn(
          'fixed right-6 bottom-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-[#1D95AD] text-white shadow-xl transition-all duration-300 hover:cursor-pointer hover:bg-[#1a8599]',
          'border border-gray-800/10 backdrop-blur-sm',
          className
        )}
        onClick={toggleOpen}
        type="button"
      >
        <MessageCircle className="h-8 w-8" />
      </button>
    </>
  )
}

function ChatMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ children, ...props }) => (
          <a
            {...props}
            className="underline underline-offset-2 hover:opacity-90"
            rel="noreferrer"
            target="_blank"
          >
            {children}
          </a>
        ),
        code: ({ className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className ?? '')
          const isBlock = Boolean(match)
          return isBlock ? (
            <code
              {...props}
              className="block rounded-md bg-black/90 px-3 py-2 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-white"
            >
              {children}
            </code>
          ) : (
            <code {...props} className="rounded bg-black/10 px-1 py-0.5 font-mono text-[12px]">
              {children}
            </code>
          )
        },
        ol: ({ children, ...props }) => (
          <ol {...props} className="list-decimal space-y-1 pl-5">
            {children}
          </ol>
        ),
        p: ({ children, ...props }) => (
          <p {...props} className="whitespace-pre-wrap">
            {children}
          </p>
        ),
        ul: ({ children, ...props }) => (
          <ul {...props} className="list-disc space-y-1 pl-5">
            {children}
          </ul>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function UiActionRenderer({
  action,
  isSending,
  onButtonClick,
  onOpenFlightResults,
}: UiActionRendererProps) {
  if (action.type === 'buttons') {
    return (
      <div className="flex flex-col gap-1.5">
        {action.prompt && (
          <p className="text-[11px] font-medium text-stone-500">{action.prompt}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {action.options.map((opt) => (
            <button
              key={opt.id}
              className={cn(
                'inline-flex items-center gap-1 rounded-2xl border border-[#1D95AD]/30 bg-white px-3 py-1.5 text-left text-[11px] font-medium text-[#1D95AD] shadow-sm transition',
                'hover:bg-[#1D95AD]/10 focus-visible:ring-2 focus-visible:ring-[#1D95AD] focus-visible:ring-offset-1 focus-visible:outline-none',
                isSending && 'cursor-not-allowed opacity-60'
              )}
              disabled={isSending}
              onClick={() => onButtonClick(opt.value)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (action.type === 'open_flight_results') {
    return (
      <button
        className={cn(
          'inline-flex items-center gap-1.5 rounded-2xl border border-amber-300 bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-400 px-3 py-1.5 text-[11px] font-medium text-amber-900 shadow-sm ring-1 ring-amber-300/70 transition hover:brightness-105',
          'focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:outline-none'
        )}
        onClick={() => onOpenFlightResults(action.query)}
        type="button"
      >
        <ExternalLink className="h-3 w-3" />
        Voir les résultats de vols
      </button>
    )
  }

  // show_quick_actions is handled in the store, nothing to render inline.
  return null
}
